import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../lib/api';

const AuthContext = createContext(null);

const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.heliconflow.app';

// Persist the refresh token. In addition to localStorage (origin-scoped), mirror
// it into a cookie scoped to the PARENT domain so sibling subdomains — e.g.
// docs.heliconflow.app — can read the session for SSO. The cookie is JS-readable
// by design (same exposure as localStorage); the API ignores cookies and
// authenticates via the Bearer access token. The hostname guard keeps localhost
// dev unaffected (no Secure/parent-domain cookie there).
function setRefreshToken(rt) {
  localStorage.setItem('refreshToken', rt);
  if (window.location.hostname.endsWith('heliconflow.app')) {
    document.cookie = 'hf_refresh=' + rt + '; Domain=.heliconflow.app; Path=/; Max-Age=604800; Secure; SameSite=Lax';
  }
}
function clearRefreshToken() {
  localStorage.removeItem('refreshToken');
  if (window.location.hostname.endsWith('heliconflow.app')) {
    document.cookie = 'hf_refresh=; Domain=.heliconflow.app; Path=/; Max-Age=0; Secure; SameSite=Lax';
  }
}

// Decode JWT payload to extract user info (server handles verification)
function decodeToken(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id:                payload.sub,
      name:              payload.name,
      email:             payload.email,
      initials:          payload.initials,
      roles:             payload.roles || [],
      mustChangePassword: payload.mustChangePassword || false,
      isExternal:        payload.isExternal         || false,
      assignedClientIds: payload.assignedClientIds  || [],
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Keep axios instance auth header in sync
  useEffect(() => {
    api.setToken(accessToken);
  }, [accessToken]);

  const logout = useCallback(() => {
    const rt = localStorage.getItem('refreshToken');
    if (rt) {
      // Fire-and-forget — clears the token hash on the server
      api.post('/api/auth/logout', { refreshToken: rt }).catch(() => {});
    }
    setUser(null);
    setAccessToken(null);
    clearRefreshToken();
  }, []);

  // On mount: try to restore session from stored refresh token
  useEffect(() => {
    const rt = localStorage.getItem('refreshToken');
    if (!rt) {
      setLoading(false);
      return;
    }
    // Use raw axios to avoid the interceptor loop on this call
    axios.post(`${BASE_URL}/api/auth/refresh`, { refreshToken: rt })
      .then(res => {
        setAccessToken(res.data.accessToken);
        // Refresh rotates the token — update localStorage
        setRefreshToken(res.data.refreshToken);
        setUser(decodeToken(res.data.accessToken));
      })
      .catch(() => {
        clearRefreshToken();
      })
      .finally(() => setLoading(false));
  }, []);

  // 401 interceptor — auto-refresh access token and retry original request
  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      res => res,
      async err => {
        const original = err.config;
        if (err.response?.status === 401 && !original._retry) {
          original._retry = true;
          const rt = localStorage.getItem('refreshToken');
          if (rt) {
            try {
              // Raw axios to avoid triggering this interceptor recursively
              const res = await axios.post(`${BASE_URL}/api/auth/refresh`, { refreshToken: rt });
              setAccessToken(res.data.accessToken);
              setRefreshToken(res.data.refreshToken);
              setUser(decodeToken(res.data.accessToken));
              original.headers['Authorization'] = `Bearer ${res.data.accessToken}`;
              return api(original);
            } catch {
              logout();
            }
          } else {
            logout();
          }
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(interceptorId);
  }, [logout]);

  const login = async (email, password) => {
    const res = await api.post('/api/auth/login', { email, password });
    setAccessToken(res.data.accessToken);
    // Merge mustChangePassword from the login response into the decoded user
    const decoded = decodeToken(res.data.accessToken);
    setUser({ ...decoded, mustChangePassword: res.data.user.mustChangePassword });
    setRefreshToken(res.data.refreshToken);
  };

  // Call this after a successful change-password to update in-memory state
  const clearMustChangePassword = (newAccessToken, newRefreshToken, updatedUser) => {
    setAccessToken(newAccessToken);
    setUser({ ...decodeToken(newAccessToken), mustChangePassword: false, ...updatedUser });
    setRefreshToken(newRefreshToken);
  };

  // Call this after any profile update that re-issues tokens (e.g. name change)
  const refreshSession = useCallback((newAccessToken, newRefreshToken) => {
    setAccessToken(newAccessToken);
    setUser(decodeToken(newAccessToken));
    setRefreshToken(newRefreshToken);
  }, []);

  // Role helper — checks if the current user has a given role
  const hasRole = (...roles) => {
    if (!user) return false;
    return roles.some(r => user.roles?.includes(r));
  };

  return (
    <AuthContext.Provider value={{ user, accessToken, login, logout, loading, hasRole, clearMustChangePassword, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
